import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { ColumnFilterValuesMenu } from '../../../components/Table/Filters/ColumnFilterValuesMenu'
import { api } from '../../../services/api'
import { Button } from '../../../components/Button'
import { SearchInput } from '../../../components/SearchInput'
import { TableWithInfiniteScroll } from './components/TableWithInfiniteScroll'
import { TableWithManuallyPagination } from './components/TableWithManuallyPagination'
import { TableRowActions } from './components/TableRowActions'
import { DialogModal } from '../../../components/DialogModal'
import { RequestForm } from './components/RequetForm'
import { useRequestDeleteMutation } from '../../../services/hooks/request/mutations/useRequestDeleteMutation'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import { Medias } from '../../../enumns/medias'
import { handlerType } from '../../../enumns/handlerType'
import { type Column } from '../../../components/Table'
import { type Filters } from '../Database'
import { invalidateQueriesList } from '../../../utils/invalidateQueriesList'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../constants/toast'
import { getErrorMessage } from '../../../utils/getErrorMessage'
import { type RequestDomain } from '../../../types/domain/request-domain'

export const Requests = () => {
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`)
  const deleteRequestMutation = useRequestDeleteMutation()
  const queryClient = useQueryClient()

  const [openCreateRequestModal, setOpenCreateRequestModal] =
    useState<boolean>(false)
  const [filters, setFilters] = useState<Filters>({
    page: 1,
    take: 10,
    search: '',
  })

  const handleApplyColumnFilter = (
    filterName: string,
    filteredValues: string[] | boolean[]
  ) => {
    setFilters((prevState) => ({
      ...prevState,
      [filterName]: filteredValues.length === 0 ? [''] : filteredValues,
      page: 1,
    }))
  }

  const handleClearColumnFilter = (filterName: string) => {
    setFilters((prevState) => {
      if (prevState[filterName]) {
        return {
          ...prevState,
          [filterName]: null,
          page: 1,
        }
      }

      return prevState
    })
  }

  const columns = useMemo(() => {
    return [
      {
        field: 'id',
        headerName: 'ID',
      },
      {
        field: 'responsible',
        headerName: 'Responsável',
        hasMenuFilter: true,
        renderMenuFilter ({ isMenuOpen, dataTrigger, onCloseMenu }) {
          return (
            <ColumnFilterValuesMenu
              isOpen={isMenuOpen}
              dataTrigger={dataTrigger}
              cacheKey="@requests/responsibleNames"
              onClearFilter={() => handleClearColumnFilter('responsibleNames')}
              onApplyFilter={(values) =>
                handleApplyColumnFilter('responsibleNames', values)
              }
              onClose={onCloseMenu}
              onFetchColumnValues={async (page: number, search: string) => {
                const url = `/requests/responsibles/get-column-values?column=name&page=${page}&search=${search}`
                const response = await api.get(url)
                return response.data
              }}
            />
          )
        },
      },
      {
        field: 'client',
        headerName: 'Cliente',
        hasMenuFilter: true,
        renderMenuFilter ({ isMenuOpen, dataTrigger, onCloseMenu }) {
          return (
            <ColumnFilterValuesMenu
              isOpen={isMenuOpen}
              dataTrigger={dataTrigger}
              cacheKey="@requests/clientNames"
              onClearFilter={() => handleClearColumnFilter('clientNames')}
              onApplyFilter={(values) =>
                handleApplyColumnFilter('clientNames', values)
              }
              onClose={onCloseMenu}
              onFetchColumnValues={async (page: number, search: string) => {
                const url = `/requests/clients/get-column-values?column=name&page=${page}&search=${search}`
                const response = await api.get(url)
                return response.data
              }}
            />
          )
        },
      },
      {
        field: 'requester',
        headerName: 'Solicitante',
        hasMenuFilter: true,
        renderMenuFilter ({ isMenuOpen, dataTrigger, onCloseMenu }) {
          return (
            <ColumnFilterValuesMenu
              isOpen={isMenuOpen}
              dataTrigger={dataTrigger}
              cacheKey="@requests/requesterNames"
              onClearFilter={() => handleClearColumnFilter('requesterNames')}
              onApplyFilter={(values) =>
                handleApplyColumnFilter('requesterNames', values)
              }
              onClose={onCloseMenu}
              onFetchColumnValues={async (page: number, search: string) => {
                const url = `/requests/requesters/get-column-values?column=name&page=${page}&search=${search}`
                const response = await api.get(url)
                return response.data
              }}
            />
          )
        },
      },
      {
        field: 'project',
        headerName: 'Projeto',
        hasMenuFilter: true,
        renderMenuFilter ({ isMenuOpen, dataTrigger, onCloseMenu }) {
          return (
            <ColumnFilterValuesMenu
              isOpen={isMenuOpen}
              dataTrigger={dataTrigger}
              cacheKey="@requests/projectNames"
              onClearFilter={() => handleClearColumnFilter('projectNames')}
              onApplyFilter={(values) =>
                handleApplyColumnFilter('projectNames', values)
              }
              onClose={onCloseMenu}
              onFetchColumnValues={async (page: number, search: string) => {
                const url = `/requests/get-column-values?column=projectName&page=${page}&search=${search}`
                const response = await api.get(url)
                return response.data
              }}
            />
          )
        },
      },
      {
        field: 'asset',
        headerName: 'Asset',
        hasMenuFilter: true,
        renderMenuFilter ({ isMenuOpen, dataTrigger, onCloseMenu }) {
          return (
            <ColumnFilterValuesMenu
              isOpen={isMenuOpen}
              dataTrigger={dataTrigger}
              cacheKey="@requests/assetNames"
              onClearFilter={() => handleClearColumnFilter('assetNames')}
              onApplyFilter={(values) =>
                handleApplyColumnFilter('assetNames', values)
              }
              onClose={onCloseMenu}
              onFetchColumnValues={async (page: number, search: string) => {
                const url = `/requests/assets/get-column-values?column=fullName&page=${page}&search=${search}`
                const response = await api.get(url)
                return response.data
              }}
            />
          )
        },
      },
      {
        field: 'analyst',
        headerName: 'Analista',
        hasMenuFilter: true,
        renderMenuFilter ({ isMenuOpen, dataTrigger, onCloseMenu }) {
          return (
            <ColumnFilterValuesMenu
              isOpen={isMenuOpen}
              dataTrigger={dataTrigger}
              cacheKey="@requests/assetAnalystNames"
              onClearFilter={() => handleClearColumnFilter('assetAnalystNames')}
              onApplyFilter={(values) =>
                handleApplyColumnFilter('assetAnalystNames', values)
              }
              onClose={onCloseMenu}
              onFetchColumnValues={async (page: number, search: string) => {
                const url = `/requests/analysts/get-column-values?column=fullName&page=${page}&search=${search}`
                const response = await api.get(url)
                return response.data
              }}
            />
          )
        },
      },
      {
        field: 'date',
        headerName: 'Data de entrada',
      },
      {
        field: 'description',
        headerName: 'Descrição',
        maxWidthInPx: 200,
      },
      {
        field: 'action',
        headerName: 'Ação',
        renderCell ({ id, hasBecomeProject }: RequestDomain) {
          return (
            <TableRowActions
              hasBecomeProject={hasBecomeProject}
              onRemove={() => handleDeleteRequest(id)}
              isLoadingRemove={deleteRequestMutation.isLoading}
              id={id}
            />
          )
        },
      },
    ] as Column[]
  }, [])

  useEffect(() => {
    if (!isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        page: 1,
      }))
    }
  }, [isMinScreenWidthMD])

  // Handler
  const handleCreateRequest = () => {
    setOpenCreateRequestModal(true)
  }

  const handleSearchRequest = (therm: string) => {
    setFilters((prevState) => ({
      ...prevState,
      page: 1,
      search: therm,
    }))
  }

  const handleDeleteRequest = (id: number) => {
    deleteRequestMutation.mutate(id, {
      onSuccess () {
        invalidateQueriesList(queryClient, [
          ['requests'],
          '@requests/responsibleNames',
          '@requests/clientNames',
          '@requests/requesterNames',
          '@requests/projectNames',
          '@requests/assetNames',
          '@requests/assetAnalystNames',
        ])
        toast('Request removido com sucesso', TOAST_SUCCESS_DEFAULT_OPTIONS)
      },
      onError (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  return (
    <div className="flex flex-col mx-8 my-10">
      <div className="w-full md:flex md:items-center md:justify-between">
        <h1 className="font-medium text-grey-900 md:text-lg">Requests</h1>
        <div className="md:flex md:gap-2">
          <SearchInput
            onSearch={handleSearchRequest}
            placeholder="Buscar por responsável ou cliente"
            className="mb-6 md:w-96 md:mb-0"
          />

          <Button className="h-10 ml-auto" onClick={handleCreateRequest}>
            Cadastrar
          </Button>
        </div>
      </div>

      <div className="mt-6">
        {isMinScreenWidthMD ? (
          <TableWithManuallyPagination
            columns={columns}
            filters={filters}
            setFilters={setFilters}
          />
        ) : (
          <TableWithInfiniteScroll columns={columns} filters={filters} />
        )}
      </div>

      {/* Modal Create Request */}
      <DialogModal
        isOpen={openCreateRequestModal}
        onClose={() => setOpenCreateRequestModal(false)}
        title="Cadastrar request"
      >
        <RequestForm
          type={handlerType.SAVE}
          onClose={() => setOpenCreateRequestModal(false)}
        />
      </DialogModal>
    </div>
  )
}
